import Image from 'next/image'

import styles from './styles.module.scss'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBookmark, faEye, faShare, faXmark } from '@fortawesome/free-solid-svg-icons'

import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import AuctionListing from '../AuctionListing/AuctionListing'
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner'

import { useSocket } from '../../../Context/socket'
import { useSession } from 'next-auth/react'
import axios from 'axios'
import { BarLoader, PacmanLoader, PuffLoader, PulseLoader } from 'react-spinners'
import errorMsg from '../../../Functions/errorMsg'
import successMsg from '../../../Functions/successMsg'
import { isEqual } from 'lodash'

import NumberFormat from 'react-number-format';
import { useRouter } from 'next/router'

export default function WatchList({ setNavCategory, setPendingRequest }) {

  const { socket } = useSocket();
  const { data: session, status } = useSession();

  const [initialLoading, setInitialLoading] = useState(null);
  const [watchingListings, setWatchingListings] = useState(null);

  useEffect(() => {
    if(initialLoading !== null) return;
    if(watchingListings !== null) return;
    if(status === 'loading') return;
    if(!session) return;

    async function getMyListings() {
        setInitialLoading(true);
        setPendingRequest(true);

        const result = await axios.post(`${window.location.origin}/api/auctionHub/savedAuctions`, {
          userID: session.user.token.id
        }).catch((error) => {
            errorMsg(error?.response?.data?.status || error.toString())
            setPendingRequest(false);
        })

        if(result && result.status === 200) {
          const data = result.data;
          setWatchingListings(data);
          setPendingRequest(false);
        }

    }

    getMyListings();

  }, [session, status])

  function isLoading() {
    if(status === 'loading') return true;
    if(initialLoading === false || initialLoading === null) return true;
    if(watchingListings === null) return true;
    return false;
  }

  function handleWatch(type, listingID, username) {
    if(type === 'remove') {
      const listingPos = watchingListings.findIndex((listing) => listing._id === listingID);
      if(listingPos > -1) {
        const updatedWatchlist = [...watchingListings];
        updatedWatchlist[listingPos].watching.filter((user) => user !== username);
        setWatchingListings(updatedWatchlist);

      } else {
        errorMsg("Oops! We could not remove this listing, try refreshing the page.");
      }
    }
    if(type === 'add') {
      const listingPos = watchingListings.findIndex((listing) => listing._id === listingID);
      if(listingPos > -1) {
        const updatedWatchlist = [...watchingListings];
        updatedWatchlist[listingPos].watching.push(username);
        setWatchingListings(updatedWatchlist);

      } else {
        errorMsg("Oops! We could not remove this listing, try refreshing the page.");
      }
    }
  }

  const [viewListingData, setViewListingData] = useState(null); // ListingID Stored

  return (
    <section id={styles.wrapper}>
      { viewListingData !== null && <AuctionListing socket={socket} listingID={viewListingData} closeListing={setViewListingData} /> }

      { isLoading() === true && <section id={styles.loading}><PulseLoader size={30} color='#154734' /></section> }

      { isLoading() === false && (watchingListings.length === 0 ?
        <div id={styles.noListings}>
          <h1>Your watchlist is empty</h1>
        </div>
        :
        <section id={styles.container}>
          {
            watchingListings.map((listing, index) => {
              return <ListingCard key={index} listing={listing} setViewListingData={setViewListingData} handleWatch={handleWatch} />
            })
          }
        </section>
      )}

  </section>
  )
}

const ListingCard = ({ listing, setViewListingData, handleWatch }) => {

  const router = useRouter();

  const { data: session, status } = useSession();
  const { auctionSocketConnected, auctionSocket } = useSocket();

  const username = session.user.token.username;
  const listingID = listing._id;

  const [coverLoaded, setCoverLoaded] = useState(false);
  const getCover = listing.images.findIndex((images) => images.position === 1);

  const [watching, setWatching] = useState(listing.watching.includes(username));

  const [watchPending, setWatchPending] = useState(false);

  async function apiHandleWatch() {
    if(watchPending === true) return;
    setWatchPending(true);
    
    const type = watching === true ? 'remove' : 'add';

    if(type === "remove") {
      const result = await axios.post(`${window.location.origin}/api/auctionHub/watch-methods/unwatch`, {
        listingID: listingID,
        username: username,
      }).catch((error) => {
        errorMsg(error?.response?.data?.status || error.toString());
        setWatchPending(false)
      })
  
      if(result && result.status === 200) {
        setWatchPending(false)
        handleWatch('remove', listingID, username);
        successMsg("Listing unsaved.");
        setWatching(false);
      }
    }

    if(type === "add") {
      const result = await axios.post(`${window.location.origin}/api/auctionHub/watch-methods/watch`, {
        listingID: listingID,
        username: username,
      }).catch((error) => {
        errorMsg(error?.response?.data?.status || error.toString());
        setWatchPending(false)
      })
  
      if(result && result.status === 200) {
        setWatchPending(false)
        handleWatch('add', listingID, username);
        successMsg("Listing unsaved.");
        setWatching(true);
      }
    }


  }

  const [bids, setBids] = useState(listing.bids.length > 0 ? listing.bids.sort((a, b) => b.bid - a.bid) : []);

  const [loadingBids, setLoadingBids] = useState(false);

  useEffect(() => {
    async function setupBidCAD() {
      if (!listingID) {
        return errorMsg("There was an error loading the bidding data for this auction, please try again later.");
      }

      setLoadingBids(true);

      const getAuctionBids = await axios.post(`${window.location.origin}/api/auctionHub/helpers/getAuctionBids`, {
        auctionID: listingID,
      }).catch((error) => {
        errorMsg(error?.response?.data?.status || error.toString());
      });

      if (getAuctionBids.status === 200) {
        const result = getAuctionBids.data;
        setBids(result);
      } else {
        setBids(false);
      }

      setLoadingBids(false);
    }
  
    setupBidCAD();

  }, [listingID]);

  const [auctionState, setAuctionState] = useState(null);

  function handleIncomingBid(data) {
    const incomingAuctionID = data.auctionID;
    if(incomingAuctionID !== listingID) return;
    const username = data.username;
    const bid = data.bid;

    const newBid = { incomingAuctionID, username, bid };
    const updatedBids = [...bids, newBid];

    updatedBids.sort((a, b) => b.bid - a.bid);

    setBids(updatedBids);
  }

  function getHighestBid() {
    if(bids.length === 0) return { bid: 0.00 }
    const sortedBidsArray = [...bids].sort((a, b) => b.bid - a.bid);
    if(!isEqual(sortedBidsArray, bids)) {
      setBids(sortedBidsArray);
    }

    return sortedBidsArray[0]
  }

  useEffect(() => {
    if(!auctionSocket || !auctionSocketConnected) return;
    if(auctionState !== 'Counting Down') return;

      const auctionRoomID = listingID;

      auctionSocket.emit('join-auction-room', { roomID: auctionRoomID });

    return () => {
      auctionSocket.emit('leave-auction-room', { roomID: auctionRoomID });
    };
  }, [auctionSocket, auctionState, auctionSocketConnected]);

  useEffect(() => {
    if(!auctionSocket) return;
    if(auctionState !== 'Counting Down') return;
    
    auctionSocket.on('incomingBid', handleIncomingBid);

    return () => {
      auctionSocket.off('incomingBid');
    };
  }, [auctionSocket, auctionState])

  async function getFinalBidArray() {
    if(loadingBids) return;

    setLoadingBids(true);

    const getAuctionBids = await axios.post(`${window.location.origin}/api/auctionHub/helpers/getAuctionBids`, {
      auctionID: listingID,
    }).catch((error) => {
      errorMsg(error?.response?.data?.status || error.toString());
    });

    if (getAuctionBids.status === 200) {
      const result = getAuctionBids.data;
      setBids(result);
    }

    setLoadingBids(false);
  }

  const [userBid, setUserBid] = useState('');
  const [pendingBid, setPendingBid] = useState(false);

  async function submitBid(e) {
    e.preventDefault();
    if(!userAuthenticated()) return router.push(`/account/login`);
    if(pendingBid === true) return;
    if(userBid.length === 0) return errorMsg("Please enter your bid before submitting.");

    const submittedBid = userBid;

    const bidDataRaw = {
      auctionID: listingID,
      bidID: null,
      userID: session.user.token.id,
      username: session.user.token.username,
      bid: submittedBid,
      created: new Date().toISOString()
    }

    setPendingBid(true);

    const submitBidResult = await axios.post(`${window.location.origin}/api/auctionHub/helpers/submitBid`, bidDataRaw).catch((error) => {
      errorMsg(error?.response?.data?.status || error.toString())
    });

    if(submitBidResult && submitBidResult.status === 200) {
      const verifiedBidData = submitBidResult.data;
      auctionSocket.emit('submitBid', { auctionID: verifiedBidData.auctionID, username: verifiedBidData.username, bid: verifiedBidData.bid })
      setUserBid('');
      successMsg("Your bid was submitted.");
    }

    setPendingBid(false);

  }

  const priceValueCap = (inputObj) => {
    const { value } = inputObj;
    if (value <= 100000000) return true;
    return false;
  };

  function userAuthenticated() {
    if(!session) return false;
    if(status !== 'authenticated') return false;
    if(!session.user.token.id) return false;
    return true;
  }

  return (
    <div className={styles.card}>

     <section>
        <TimeStampContainer duration={listing.auctionDuration} created={listing.creationDate} handleGetFinalBidsArray={getFinalBidArray} auctionState={auctionState} setAuctionState={setAuctionState} />
        <div style={{display: coverLoaded === false ? 'flex' : 'none'}}> <LoadingSpinner type="pulse" color="#154734" /></div>
        <Image sizes='auto auto' fill src={listing.images[getCover].url} alt={listing.images[getCover].url} onLoadingComplete={() => setCoverLoaded(true)} />
      </section>

      { (loadingBids || !auctionState) ?
        <section style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <BarLoader color='black' />
        </section>
        :
        <section>
          <div>
            <h1>{listing.title}</h1>
            { auctionState === 'Auction Closed' && <h2>{bids.length === 0 ? <b>No Winner</b> : <> <b>{getHighestBid().username === session.user.token.username ? 'You' : getHighestBid().username}</b> won with bid ${parseFloat(getHighestBid().bid).toLocaleString()} </> }</h2> }
            { auctionState !== 'Auction Closed' && <h2><b>{bids.length === 0 ? 'Bid' : 'Bid'}</b>: ${bids.length === 0 ? listing.price : parseFloat(getHighestBid().bid.toLocaleString()).toLocaleString()} </h2> }
            
            { listing.owner !== session?.user?.token?.username &&
              <div style={{display: auctionState === 'Counting Down' ? 'flex' : 'none'}}>
                <NumberFormat onValueChange={(values, sourceInfo) => { !pendingBid && setUserBid(values.value)}} isAllowed={priceValueCap} decimalSeparator={false} placeholder="Max Bid" thousandSeparator={true} prefix={'$'} value={userBid} />
                <button disabled={pendingBid} onClick={submitBid}>
                  { pendingBid ? <PacmanLoader size={10} color='white' /> : 'Quick Bid' }
                </button>
              </div>
            }

          </div>

          <div>
            <motion.button onClick={() => setViewListingData(listingID)}><FontAwesomeIcon icon={faEye} id={styles.icon} /></motion.button>

            <motion.button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/?popup=sharedPost&_id=${listing._id}`); successMsg("Link Copied") } }><FontAwesomeIcon icon={faShare} id={styles.icon} /></motion.button>

            <motion.button onClick={() => apiHandleWatch()}>
              { watchPending === false && <>
                <FontAwesomeIcon style={{marginRight: watching === true ? '8px' : '0px'}} icon={faBookmark} id={styles.icon} />
                { watching === true && <FontAwesomeIcon icon={faXmark} id={styles.icon2} /> }
              </> }
              { watchPending === true && <PuffLoader size={25} color='black' /> }
            </motion.button>
          </div>

        </section>
      }
    </div>
  )
}

const TimeStampContainer = ({ duration, created, handleGetFinalBidsArray, auctionState, setAuctionState }) => {
  const [timeStamps, setTimeStamps] = useState(null);

  useEffect(() => {
    if(duration === null || duration === undefined || created === null || created === undefined) return;

    const countdownInterval = setInterval(() => {

      const timestamp = getTimeStamp(duration, created);

      if(timestamp === 'Auction Closed' || timestamp === 'Calculating winner...') {
        if(auctionState !== timestamp) {
          setAuctionState(timestamp);
        }
      } else {
        if(auctionState !== 'Counting Down') {
          setAuctionState("Counting Down")
        }
      }

      if(timestamp === 'Auction Closed') {
        closeCountdown();
        handleGetFinalBidsArray();
        setTimeStamps("Auction Closed");
        return;
      }

      setTimeStamps(timestamp);
    }, 100);

    function closeCountdown() {
      clearInterval(countdownInterval);
    }

    return () => {
      clearInterval(countdownInterval)
    }

  }, [])

  return timeStamps !== null && <h1>{timeStamps}</h1>;
}

function getTimeStamp(duration, created) {

  const durDays = parseInt(duration);


  const ending_date = new Date(Date.parse(created));
  ending_date.setDate(ending_date.getDate() + durDays);

  const current_day = new Date();

  const total = ending_date - Date.parse(current_day); // Future ending date is subtracting by current date to get proper values

  const seconds = Math.floor( (total/1000) % 60 );
  const minutes = Math.floor( (total/1000/60) % 60 );
  const hours = Math.floor( (total/(1000*60*60)) % 24 );
  const days = Math.floor( total/(1000*60*60*24) );

  var returnSeconds;
  var returnMinutes;
  var returnHours;
  var returnDays;

  if(seconds < 0) {
    returnSeconds = 0;
  } else {
    returnSeconds = seconds;
  }

  if(minutes < 0) {
    returnMinutes = 0;
  } else {
    returnMinutes = minutes;
  }

  if(hours < 0) {
    returnHours = 0;
  } else {
    returnHours = hours;
  }

  if(days < 0) {
    returnDays = 0;
  } else {
    returnDays = days;
  }

  var state = null;

  if(seconds <= 0 && total >= -15000) {
    state = "Calculating winner..."
  }

  if(total < -15000) {
    state = "Auction Closed"
  }

  if(total > 0) {
    state = `${returnDays}d : ${returnHours}hr : ${returnMinutes}m : ${returnSeconds}s`
  }

  return state
}